import React, { useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Box, AppBar, Toolbar, IconButton, Typography, Drawer, useMediaQuery, useTheme } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import AppDrawer from '../components/AppDrawer';

// Set drawer width to match main app
const drawerWidth = 240;

const MainLayout: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();

  // Get current page title from location pathname
  const getPageTitle = () => {
    const path = location.pathname;
    if (path.includes('dashboard')) return 'Finance Dashboard';
    if (path.includes('expenses')) return 'Expenses';
    if (path.includes('income')) return 'Income';
    if (path.includes('gl-accounts')) return 'GL Accounts';
    if (path.includes('trial-balance')) return 'Trial Balance';
    if (path.includes('reports')) return 'Reports';
    if (path.includes('daily-closing')) return 'Daily Closing';
    if (path.includes('invoices')) return 'Invoices';
    if (path.includes('settings')) return 'Settings';
    return 'Finance Management';
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          bgcolor: 'white',
          color: 'text.primary',
          boxShadow: 1
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div">
            {getPageTitle()}
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Mobile drawer */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          <AppDrawer onClose={handleDrawerToggle} />
        </Drawer>

        {/* Permanent drawer for larger screens */}
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          <AppDrawer />
        </Drawer>
      </Box>

      {/* Main content area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          bgcolor: '#f8f8f8'
        }}
      >
        <Toolbar /> {/* This provides spacing below the fixed AppBar */}
        <Outlet /> {/* This will render the child routes */}
      </Box>
    </Box>
  );
};

export default MainLayout;